import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Columns3, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { useElementoColumnPrefs } from '@/hooks/useElementoColumnPrefs';

interface Props {
  /** Sólo permite mostrar/ocultar columnas cuyo id esté en este Set. */
  allowedIds?: Set<string>;
}

/**
 * Selector de columnas: marcar/desmarcar visibilidad y reordenar
 * con flechas. Se persiste por usuario en localStorage.
 */
export const ColumnasElementosPicker: React.FC<Props> = ({ allowedIds }) => {
  const { prefs, orderedColumns, toggle, move, reset } = useElementoColumnPrefs({ allowedIds });


  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Columns3 className="h-4 w-4" />
          Columnas
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 bg-popover border border-border z-50"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-medium">Personalizar columnas</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={reset}
          >
            <RotateCcw className="h-3 w-3" />
            Restablecer
          </Button>
        </div>
        <div className="max-h-80 overflow-y-auto py-1">
          {orderedColumns.map((col, idx) => {
            const visible = !prefs.hidden.includes(col.id);
            return (
              <div
                key={col.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50"
              >
                <Checkbox
                  id={`col-${col.id}`}
                  checked={visible}
                  onCheckedChange={() => toggle(col.id)}
                />
                <label
                  htmlFor={`col-${col.id}`}
                  className="flex-1 text-sm cursor-pointer select-none"
                >
                  {col.label}
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={idx === 0}
                  onClick={() => move(col.id, -1)}
                  aria-label="Mover arriba"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={idx === orderedColumns.length - 1}
                  onClick={() => move(col.id, 1)}
                  aria-label="Mover abajo"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
